import { Dropdown, Flex, Grid, Menu, Tooltip } from "antd";
import React, { useCallback, useContext, useMemo, useState } from "react";
import Input from "../../components/custom/input/input/Input";
import HSelect from "../../components/custom/input/select/Select";
import HTableWithTabs from "../../components/custom/table/HTableWithTabs";
import { AppContext } from "../../context/AppContext";
import { DeleteComponent, EmptyListView, formatDate } from "../utils/CommonVessels";
import {
    STATUS_FILTER_OPTIONS,
    TRIGGER_FILTER_OPTIONS,
    type UseWorkflowsResult,
    type WorkflowRecord,
} from "./hooks/useWorkflows";

export type { WorkflowApiRecord, WorkflowRecord, WorkflowTriggerType } from "./hooks/useWorkflows";

export interface TriggerRulesListProps {
    workflows: UseWorkflowsResult;
    onWorkflowClick: (workflow: WorkflowRecord) => void;
    onNewWorkflow: () => void;
}

function workflowStatusStyleKey(status: string): string {
    const s = (status ?? "").trim().toLowerCase();
    if (s === "paused") return "disabled";
    return s;
}

function workflowStatusDisplayLabel(status: string): string {
    const s = (status ?? "").trim().toLowerCase();
    if (s === "paused") return "Disabled";
    if (!status) return status;
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

const TriggerRulesList: React.FC<TriggerRulesListProps> = ({ workflows, onWorkflowClick, onNewWorkflow }) => {
    const [appProperties] = useContext(AppContext);
    const {
        requestWorkflows,
        activeTab,
        searchInputValue,
        setSearchInputValue,
        appliedSearchKeyword,
        triggerTypeFilter,
        statusFilter,
        handleTriggerTypeFilterChange,
        handleStatusFilterChange,
        handleSearchApply,
        handleSearchClear,
        handleTabChange,
        deleteWorkflow,
        tableRef,
    } = workflows;

    const [deleteModalRecord, setDeleteModalRecord] = useState<WorkflowRecord | null>(null);
    const [visibleDropdown, setVisibleDropdown] = useState<string | null>(null);
    const { md: greaterThanMediumScreen } = Grid.useBreakpoint();

    const handleMenuClick = useCallback((record: WorkflowRecord, open: boolean) => {
        setVisibleDropdown(open ? record.workflowId : null);
    }, []);

    const listRestrictedByQuery =
        appliedSearchKeyword.trim().length > 0 || triggerTypeFilter != null || statusFilter != null;

    const emptyListView = useMemo(
        () => (
            <EmptyListView
                icon=""
                tagLineOne={listRestrictedByQuery ? "No rules match your search or filters" : "No rules created yet"}
                tagLineTwo={
                    listRestrictedByQuery
                        ? "Try a different name or clear filters"
                        : "Create your first rule to get started"
                }
                tagHeight="32vh"
            />
        ),
        [listRestrictedByQuery]
    );

    const handleDeleteConfirm = useCallback(
        async (record: WorkflowRecord) => {
            await deleteWorkflow(record);
            setDeleteModalRecord(null);
        },
        [deleteWorkflow]
    );

    const columns = [
        {
            title: "Rule name",
            dataIndex: "workflowName",
            key: "workflowName",
            width: "20%",
            render: (text: string) => <span className="hs-fs-14 hs-fw-600 hs-color-dark-grey">{text}</span>,
        },
        {
            title: "Trigger/Type",
            dataIndex: "triggerType",
            key: "triggerType",
            width: "20%",
            render: (_: string, record: WorkflowRecord): React.ReactNode => {
                const parts = (record.triggerType ?? "").split(".");
                const typePart = parts.length >= 2 ? parts[1] : record.triggerType;
                return (
                    <span className="hs-fs-14 hs-fw-500 hs-color-dark-grey">
                        {typePart}/{record.direction}
                    </span>
                );
            },
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: "12%",
            render: (status: string) => {
                const styleKey = workflowStatusStyleKey(status ?? "");
                return (
                    <span
                        className={`trigger-rules-list-status-pill gap-1 hs-fs-12 px-3 py-2 rounded-5 trigger-rules-list-status-${styleKey}`}
                    >
                        <span
                            className={`trigger-rules-list-status-dot rounded-circle trigger-rules-list-status-dot-${styleKey}`}
                        />
                        {workflowStatusDisplayLabel(status ?? "")}
                    </span>
                );
            },
        },
        {
            title: "Created Time",
            dataIndex: "createdTime",
            key: "createdTime",
            width: "14%",
            ellipsis: true,
            render: (_: string | undefined, record: WorkflowRecord) => {
                const raw = record.createdTime?.trim();
                if (!raw) {
                    return <span className="hs-fs-14 hs-color-medium-grey">-</span>;
                }
                const formattedTime = formatDate(raw);
                return (
                    <Tooltip title={formattedTime}>
                        <span className="hs-fs-14 hs-color-dark-grey">{formattedTime}</span>
                    </Tooltip>
                );
            },
        },
        {
            title: "Runs",
            dataIndex: "runs",
            key: "runs",
            width: "16%",
            render: (runs?: number) => (
                <span className="d-inline-flex align-items-baseline gap-1">
                    <span className="hs-fs-14 hs-fw-600 hs-color-dark-grey">{(runs ?? 0).toLocaleString()}</span>
                </span>
            ),
        },
        {
            title: "",
            dataIndex: "workflowId",
            key: "actions",
            width: "10%",
            align: "right" as const,
            render: (_: unknown, record: WorkflowRecord): React.ReactNode => {
                return (
                    <Dropdown
                        overlay={WorkflowActionsMenu({
                            record,
                            onEditWorkflow: (r) => {
                                setVisibleDropdown(null);
                                onWorkflowClick(r);
                            },
                            onRequestDelete: (r) => {
                                setDeleteModalRecord(r);
                                setVisibleDropdown(null);
                            },
                        })}
                        trigger={["click"]}
                        placement="bottomRight"
                        rootClassName="editDeleteSettingDropdown"
                        open={visibleDropdown === record.workflowId}
                        onOpenChange={(open) => handleMenuClick(record, open)}
                    >
                        <div
                            style={{ cursor: "pointer" }}
                            className="actionIconsSprite editDeleteSettingsIcon"
                            onClick={(e) => e.stopPropagation()}
                        ></div>
                    </Dropdown>
                );
            },
        },
    ];

    const WorkflowActionsMenu = (props: {
        record: WorkflowRecord;
        onEditWorkflow: (record: WorkflowRecord) => void;
        onRequestDelete: (record: WorkflowRecord) => void;
    }) => {
        const { record, onEditWorkflow, onRequestDelete } = props;

        const isPredefinedflow = activeTab === "predefined";

        return (
            <Menu>
                <Menu.Item
                    key="editWorkflow"
                    onClick={(e) => {
                        e.domEvent.stopPropagation();
                        onEditWorkflow(record);
                    }}
                >
                    <div className="actionIconsSprite editTemplate-icon">
                        <span className="ps-4" style={{ fontSize: "12px" }}>
                            Edit
                        </span>
                    </div>
                </Menu.Item>
                {!isPredefinedflow ? (
                    <Menu.Item
                        key="deleteWorkflow"
                        onClick={(e) => {
                            e.domEvent.stopPropagation();
                            onRequestDelete(record);
                        }}
                    >
                        <div className="actionIconsSprite deleteTemplate-icon">
                            <span className="ps-4" style={{ fontSize: "12px" }}>
                                Delete
                            </span>
                        </div>
                    </Menu.Item>
                ) : null}
            </Menu>
        );
    };

    const hTableWithTabsProps = {
        tableRef,
        destroyInactiveTabPane: true,
        tabMenuItems: [
            { key: "custom", label: "Custom Flows" },
            { key: "predefined", label: "Pre-Defined Flows" },
        ] as const,
        defaultActiveKey: activeTab,
        onChange: handleTabChange,
        ui: {
            columns,
            rowKey: "workflowId",
            heightToBeSubtractedFromContainerHeight: 50,
            title: (
                <Flex
                    align={greaterThanMediumScreen ? "center" : "stretch"}
                    gap={greaterThanMediumScreen ? 20 : 12}
                    vertical={!greaterThanMediumScreen}
                    style={{ width: greaterThanMediumScreen ? undefined : "100%", minWidth: 0 }}
                >
                    {activeTab === "custom" ? ( 
                    <HSelect
                        options={TRIGGER_FILTER_OPTIONS}
                        onChange={handleTriggerTypeFilterChange}
                        placeholder="Filter by trigger"
                        selectSource="trigger filters"
                        customProps={{
                            className: "mt-1",
                            style: { width: greaterThanMediumScreen ? 180 : "100%", minWidth: 0 },
                            value: triggerTypeFilter ?? "All",
                        }}
                    />
                    ) : null}
                    {activeTab === "custom" ? (
                    <HSelect
                        options={STATUS_FILTER_OPTIONS}
                        onChange={handleStatusFilterChange}
                        placeholder="Filter by status"
                        selectSource="status filters"
                        customProps={{
                            className: "mt-1",
                            style: { width: greaterThanMediumScreen ? 160 : "100%", minWidth: 0 },
                              value: statusFilter ?? "All",
                          }}
                      />
                  ) : null}
                    <Input
                        placeholder="Search by rule name"
                        allowClear
                        style={{ width: greaterThanMediumScreen ? 200 : "100%", minWidth: 0 }}
                        value={searchInputValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setSearchInputValue(e.target.value);
                            if (!e.target.value?.trim()) {
                                handleSearchClear();
                            }
                        }}
                        onPressEnter={handleSearchApply}
                    />
                </Flex>
            ),
        },
        data: {
            request: requestWorkflows,
            requestExtraParams: {
                searchKeyword: appliedSearchKeyword,
                flowType: activeTab,
                triggerType: triggerTypeFilter,
                status: statusFilter,
            },
        },
        toolbar: {
            buttonText: "Create Rule",
            buttonIcon: <div className="actionIconsSprite plusIconWhite" />,
            onButtonClick: activeTab === "custom" ? onNewWorkflow : undefined,
        },
        proProps: {
            locale: {
                emptyText: emptyListView,
            },
        },
        onRowClick: (record: WorkflowRecord) => ({
            onClick: () => onWorkflowClick(record),
            style: { cursor: "pointer" },
        }),
    };

    return (
        <Flex
            className={`trigger-rules-list-root flex-column h-100 overflow-hidden rounded-2 p-3${
                greaterThanMediumScreen ? "" : " trigger-rules-list-toolbar-compact"
            }`}
        >
            <HTableWithTabs {...hTableWithTabsProps} />

            {deleteModalRecord && (
                <DeleteComponent
                    modalOpen={!!deleteModalRecord}
                    setModalOpen={(open: boolean) => {
                        if (!open) setDeleteModalRecord(null);
                    }}
                    record={deleteModalRecord}
                    confirmDeleteIcon="hs-delete-icon"
                    confirmDeleteBtnName="Delete"
                    confirmDeleteComponentTitle="Delete Rule"
                    confirmDeleteComponent="Are you sure you want to delete this rule? This action cannot be undone."
                    appProperties={appProperties}
                    handleDelete={() => handleDeleteConfirm(deleteModalRecord)}
                />
            )}
        </Flex>
    );
};

export default TriggerRulesList;
